import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "@/lib/i18n";
import { scrollToId } from "@/lib/scroll";
import { PresentationGate } from "./Presentation";

const LINKS = ["about", "product", "cases", "economics", "faq", "contacts"];
const TARGET = { contacts: "footer-contacts" };

export const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex rounded-full border border-white/15 p-1 text-xs font-semibold" data-testid="lang-switcher">
      {[["ru", "RU"], ["kz", "ҚАЗ"]].map(([k, l]) => (
        <button
          key={k}
          data-testid={`lang-${k}-button`}
          onClick={() => setLang(k)}
          className={`px-3 py-1.5 rounded-full transition-colors duration-300 ${lang === k ? "bg-[var(--gold)] text-[#0A0D14]" : "text-[var(--text)] hover:text-[var(--gold)]"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [pres, setPres] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };
  const openPres = () => {
    setOpen(false);
    setPres(true);
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${scrolled ? "bg-[#0A0D14]/70 backdrop-blur-xl border-b border-white/10 py-3" : "py-5 border-b border-transparent"}`}>
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-center justify-between gap-6">
        <Logo onClick={() => go("top")} />
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 text-sm text-[var(--text)]/85 whitespace-nowrap">
          {LINKS.map((l) => (
            <button key={l} data-testid={`nav-${l}-link`} onClick={() => go(TARGET[l] || l)} className="nav-link">
              {t.nav[l]}
            </button>
          ))}
          <button data-testid="nav-presentation-link" onClick={openPres} className="nav-link">{t.nav.presentation}</button>
        </nav>
        <div className="flex items-center gap-3">
          <LangSwitch />
          <div className="hidden md:block lg:hidden xl:block">
            <button data-testid="header-cta-button" onClick={() => go("contacts")} className="btn-gold text-sm whitespace-nowrap">
              {t.nav.cta} <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
          <button data-testid="mobile-menu-button" aria-label={t.nav.menu} onClick={() => setOpen((v) => !v)} className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-white/15 text-[var(--text)]">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden mx-5 mt-3 rounded-3xl border border-white/10 bg-[#0E131D]/95 backdrop-blur-xl p-6 flex flex-col gap-4"
          >
            {LINKS.map((l) => (
              <button key={l} data-testid={`mobile-nav-${l}-link`} onClick={() => go(TARGET[l] || l)} className="text-left font-display text-xl text-[var(--text)]">
                {t.nav[l]}
              </button>
            ))}
            <button data-testid="mobile-nav-presentation-link" onClick={openPres} className="text-left font-display text-xl text-[var(--text)]">{t.nav.presentation}</button>
            <button data-testid="mobile-cta-button" onClick={() => go("contacts")} className="btn-gold justify-center mt-2">
              {t.nav.cta}
            </button>
          </motion.nav>
        )}
      </AnimatePresence>
      <PresentationGate open={pres} onOpenChange={setPres} />
    </header>
  );
};
